import React, { FC, useCallback, useState } from 'react'
import { Grid, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { BusinessEntityUserDto } from '@sparling/financial-common'
import { get } from 'lodash'
import { businessEntityDetailsLoading, getBusinessEntityDetailsData } from '../../../../store/selectors'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { UpdateBusinessEntityUsersModal } from '../UpdateBusinessEntityUsersModal'
import { columns } from './detailsBusinessEntityUsersList'
import { IDataItem, TableComponent } from '../../../../components/Table'
import { deleteBusinessEntityUsersRequest, getBusinessEntityDetailsRequest } from '../../../../store/actions'
import { Modal } from '../../../../components/Modal'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    marginTop: theme.spacing(4),
    padding: theme.spacing(4),
  },
  sectionName: {
    fontSize: 20,
    color: theme.palette.primary.light,
  },
}))

export const BusinessEntityUsers: FC = () => {
  const classes = useStyles()
  const { id } = useParams<{ [key: string]: string }>()
  const dispatch = useDispatch()
  const loading = useSelector(businessEntityDetailsLoading)
  const entity = useSelector(getBusinessEntityDetailsData)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [idToUnassign, setIdToUnassign] = useState<number | null>(null)
  const [isOpenUnassignModal, setOpenUnassignModal] = useState<boolean>(false)

  const loadData = useCallback(
    (page = 1) => {
      dispatch(getBusinessEntityDetailsRequest({ id, page }))
    },
    [dispatch, id],
  )

  const onRemove = useCallback((userId: number) => {
    setIdToUnassign(userId)
    setOpenUnassignModal(true)
  }, [])

  const deleteUserFromEntity = useCallback(() => {
    if (idToUnassign) {
      dispatch(deleteBusinessEntityUsersRequest({ id, userId: idToUnassign }))
    }
    setOpenUnassignModal(false)
    setIdToUnassign(null)
  }, [dispatch, id, idToUnassign])

  const onCloseUnassignModal = useCallback(() => {
    setOpenUnassignModal(false)
  }, [])

  const onCloseEditModal = useCallback(() => {
    setIsOpen(false)
  }, [])

  const onOpenEditModal = useCallback(() => {
    setIsOpen(true)
  }, [])

  return (
    <Grid container className={classes.container}>
      <Grid container item xs={12}>
        <Grid container item xs={10}>
          <Typography className={classes.sectionName}>Users are assigned to an entity</Typography>
        </Grid>
        <Grid container item xs={2} justifyContent={'flex-end'}>
          <GreenButton text="Edit" width={120} onClick={onOpenEditModal} />
        </Grid>
      </Grid>
      <Grid container item xs={12} gap={3}>
        <TableComponent
          columns={columns}
          data={get(entity?.users, 'items') as IDataItem<BusinessEntityUserDto>[]}
          params={
            get(entity?.users, 'meta') as {
              currentPage: number
              itemsPerPage: number
              itemCount: number
              totalItems: number
              totalPages: number
            }
          }
          loadData={loadData}
          loading={loading}
          isShowRemoveIcon
          onRemove={onRemove}
          isShowEditIcon={false}
          isShowInfoIcon={false}
        />
      </Grid>
      <UpdateBusinessEntityUsersModal id={id} isOpen={isOpen} modalClose={onCloseEditModal} />
      <Modal
        title={'Delete User From Entity'}
        isOpen={isOpenUnassignModal}
        modalClose={onCloseUnassignModal}
        onSubmit={deleteUserFromEntity}
      >
        Are you sure you want to delete this user?
      </Modal>
    </Grid>
  )
}
